import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ref, uploadBytes, deleteObject } from 'firebase/storage';
import { doc, setDoc, onSnapshot, updateDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { storage, db, functions } from '../firebase';
import AudioTest from './AudioTest';

interface Task {
  title: string;
  category: 'Technique' | 'Repertoire';
}

interface QuizQuestion {
  question: string;
  answer: string;
}

interface LessonData {
  lessonId: string;
  studentId: string;
  teacherId: string | null;
  createdAt: any;
  summaryTxt?: string; // Legacy field
  overview?: string;
  practiceReminders?: Array<{
    type: 'section' | 'subsection' | 'bullet' | 'text';
    content: string;
    level: number;
  }>; // Structured array for better display
  mindsetMessages?: string; // Now always a string
  keyQuotes?: string[]; // Array for carousel functionality
  tasks?: Task[]; // Optional legacy field
  quiz?: QuizQuestion[]; // Optional legacy field
  transcript?: string;
  status?: string;
  processedAt?: any;
}

const RecordLesson: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { studentId } = useParams<{ studentId?: string }>();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lessonData, setLessonData] = useState<LessonData | null>(null);
  const [error, setError] = useState('');
  const [recordingComplete, setRecordingComplete] = useState(false);
  const [estimatedFileSize, setEstimatedFileSize] = useState(0);
  const [targetStudent, setTargetStudent] = useState<any>(null);
  const [maxRecordingReached, setMaxRecordingReached] = useState(false);
  
  // Progress tracking states
  const [processingStage, setProcessingStage] = useState<string>('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Determine if this is teacher recording for student
  const isTeacherRecording = currentUser?.role === 'teacher' && studentId;
  const targetStudentId = isTeacherRecording ? studentId : currentUser?.uid;

  // Check if user can record lessons
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // If teacher recording for student, check permissions and fetch student info
    if (isTeacherRecording) {
      if (!currentUser.linkedStudents?.includes(studentId!)) {
        setError('You do not have permission to record lessons for this student');
        return;
      }
      fetchStudentInfo();
    } else {
      // Students need active plan
      if (currentUser.role === 'student' && currentUser.planStatus !== 'active') {
        setError('You need an active subscription to record lessons');
        return;
      }
    }
  }, [currentUser, navigate, studentId, isTeacherRecording]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Clean up timers and listeners
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const fetchStudentInfo = async () => {
    if (!studentId) return;
    
    try {
      const studentDoc = await getDoc(doc(db, 'users', studentId));
      if (studentDoc.exists()) {
        const studentData = studentDoc.data();
        setTargetStudent({
          uid: studentId,
          ...studentData
        });
        
        // Check if student has active plan
        if (studentData.planStatus !== 'active') {
          setError('This student needs an active subscription to record lessons');
          return;
        }
      } else {
        setError('Student not found');
      }
    } catch (err) {
      console.error('Error fetching student info:', err);
      setError('Failed to load student information');
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateEstimatedFileSize = (seconds: number): number => {
    // 32 kbps bitrate = 4 KB per second
    const bytesPerSecond = 4000; // 4 KB/sec at 32 kbps
    return Math.round((seconds * bytesPerSecond) / 1024); // Return size in KB
  };

  const formatFileSize = (sizeInKB: number): string => {
    if (sizeInKB < 1024) {
      return `${sizeInKB} KB`;
    } else {
      return `${(sizeInKB / 1024).toFixed(1)} MB`;
    }
  };

  const startRecording = async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000, // Lower sample rate for smaller files
        }
      });
      
      // Optimised settings for 60-minute recordings
      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 32000, // Very low bitrate for voice (32 kbps)
      };
      
      // Fallback to default if optimised options aren't supported
      let mediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(stream, options);
      } catch (e) {
        console.warn('Optimised recording not supported, using default settings');
        mediaRecorder = new MediaRecorder(stream);
      }
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        setAudioUrl(URL.createObjectURL(audioBlob));
        setRecordingComplete(true);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          setEstimatedFileSize(calculateEstimatedFileSize(newTime));
          
          // Check for 70-minute limit (4200 seconds)
          if (newTime >= 4200) {
            setMaxRecordingReached(true);
            stopRecording();
            setError('Recording automatically stopped after 70 minutes. Please start a new recording.');
          }
          
          return newTime;
        });
      }, 1000);

    } catch (err) {
      setError('Failed to access microphone. Please check permissions.');
      console.error('Error starting recording:', err);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          setEstimatedFileSize(calculateEstimatedFileSize(newTime));
          
          // Check for 70-minute limit (4200 seconds)
          if (newTime >= 4200) {
            setMaxRecordingReached(true);
            stopRecording();
            setError('Recording automatically stopped after 70 minutes. Please start a new recording.');
          }
          
          return newTime;
        });
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const processLesson = async () => {
    if (!audioBlob || !currentUser) return;

    setIsProcessing(true);
    setShowProgressModal(true);
    setError('');
    setProcessingProgress(0);
    setProcessingStage('');

    try {
      // Check Firestore connection first
      setProcessingStage('Checking connection...');
      setProcessingProgress(5);
      
      // Test Firestore connection
      try {
        await getDoc(doc(db, 'users', currentUser.uid));
      } catch (connectionError) {
        console.error('Firestore connection error:', connectionError);
        throw new Error('Unable to connect to the database. Please check your internet connection and try again.');
      }
      // Generate lesson ID
      const lessonId = `lesson_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Stage 1: Uploading audio
      setProcessingStage('Uploading audio file...');
      setProcessingProgress(20);
      
      // Upload audio to Firebase Storage
      // Try to upload audio file to Firebase Storage, fallback to simulation if Storage not available
      let audioRef = null;
      try {
        audioRef = ref(storage, `tempAudio/${currentUser.uid}/${lessonId}.webm`);
        await uploadBytes(audioRef, audioBlob);
        console.log('Audio uploaded to Firebase Storage successfully');
        setProcessingProgress(40);
      } catch (storageError) {
        console.warn('Firebase Storage not available, using simulation mode:', storageError);
        audioRef = null; // Will trigger simulation mode
        setProcessingProgress(40);
      }

      // Stage 2: Creating lesson document
      setProcessingStage('Setting up lesson document...');
      setProcessingProgress(50);
      
      // Create initial lesson document
      const lessonRef = doc(db, 'lessons', lessonId);
      await setDoc(lessonRef, {
        lessonId,
        studentId: targetStudentId,
        teacherId: isTeacherRecording ? currentUser.uid : null,
        createdAt: new Date(),
        summaryTxt: '',
        tasks: [],
        quiz: [],
        status: 'processing'
      });

      // Stage 3: Processing with AI
      setProcessingStage('Transcribing audio and generating summary...');
      setProcessingProgress(70);
      
      // Process lesson - use real AI if Storage available, otherwise simulate
      if (audioRef) {
        // Call real Cloud Function for AI processing
        const processLesson = httpsCallable(functions, 'processLesson');
        await processLesson({ lessonId, studentId: targetStudentId });
      } else {
        // Use simulation mode
        await simulateLessonProcessing(lessonRef, null);
      }
      
      // Stage 4: Finalizing
      setProcessingStage('Finalizing lesson data...');
      setProcessingProgress(90);

      // Set a timeout to prevent infinite waiting
      timeoutRef.current = setTimeout(() => {
        console.warn('Lesson processing timeout - stopping listener');
        setError('Lesson processing is taking longer than expected. Please check your lesson in the dashboard.');
        setShowProgressModal(false);
        setIsProcessing(false);
      }, 300000); // 5 minutes timeout

      // Listen for lesson updates with timeout
      const unsubscribe = onSnapshot(lessonRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data() as LessonData;
          
          // Ensure arrays are properly formatted
          const processedData = {
            ...data,
            practiceReminders: Array.isArray(data.practiceReminders) ? data.practiceReminders : [],
            keyQuotes: Array.isArray(data.keyQuotes) ? data.keyQuotes : [],
            tasks: Array.isArray(data.tasks) ? data.tasks : [],
            quiz: Array.isArray(data.quiz) ? data.quiz : []
          };
          
          setLessonData(processedData);
          
          // Check if processing is complete (check for new field structure)
          if (processedData.overview && processedData.practiceReminders && 
              processedData.mindsetMessages && processedData.keyQuotes) {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            setProcessingStage('Lesson processing complete!');
            setProcessingProgress(100);
            
            // Close progress modal after a short delay
            setTimeout(() => {
              setShowProgressModal(false);
              setIsProcessing(false);
            }, 1500);
            
            unsubscribe(); // Stop listening
          }
        }
      }, (error) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        console.error('Error listening to lesson updates:', error);
        setError('Failed to receive lesson updates. Please refresh the page.');
        setShowProgressModal(false);
        setIsProcessing(false);
      });

      // Store unsubscribe function for cleanup
      unsubscribeRef.current = unsubscribe;

    } catch (err) {
      setError('Failed to process lesson. Please try again.');
      console.error('Error processing lesson:', err);
      setShowProgressModal(false);
      setIsProcessing(false);
    }
  };

  const simulateLessonProcessing = async (lessonRef: any, audioRef: any) => {
    try {
      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Generate mock AI content
      const mockData = {
        summaryTxt: `This lesson focused on fundamental music techniques and repertoire development. 
        The student demonstrated good progress in technical exercises and showed improvement in musical expression. 
        Key areas covered included scales, arpeggios, and work on classical repertoire. 
        The student should continue practicing daily to maintain momentum.`,
        tasks: [
          { title: "Practice C major scale, 2 octaves, hands together", category: "Technique" as const },
          { title: "Work on arpeggios in C, G, and D major", category: "Technique" as const },
          { title: "Continue studying Beethoven Sonata No. 1, 1st movement", category: "Repertoire" as const },
          { title: "Focus on dynamics and phrasing in assigned piece", category: "Repertoire" as const }
        ],
        quiz: [
          { question: "What key signature did we focus on in this lesson?", answer: "C major (no sharps or flats)" },
          { question: "What technical element needs the most practice?", answer: "Rhythm accuracy in the left hand" },
          { question: "Which composer's work was discussed?", answer: "Ludwig van Beethoven" },
          { question: "What should the student focus on for next week?", answer: "Technical exercises and maintaining consistent tempo" },
          { question: "How many octaves should the C major scale be practiced?", answer: "2 octaves, hands together" }
        ],
        status: 'completed',
        processedAt: new Date()
      };

      // Update Firestore with mock data
      await updateDoc(lessonRef, mockData);

      // Delete audio file (privacy requirement) - only if it exists
      if (audioRef) {
        await deleteObject(audioRef);
      }

    } catch (error) {
      console.error('Error in simulation:', error);
      await updateDoc(lessonRef, {
        status: 'failed',
        error: 'Simulation failed'
      });
    }
  };

  const resetRecording = () => {
    setRecordingComplete(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setLessonData(null);
    setIsProcessing(false);
    setRecordingTime(0);
    setError('');
    setEstimatedFileSize(0);
    setMaxRecordingReached(false);
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <nav className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-0">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="text-3xl mr-3">🎙️</div>
                <h1 className="text-xl font-bold text-white">
                  {isTeacherRecording ? `Record Lesson for ${targetStudent?.name || 'Student'}` : 'Record Lesson'}
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(isTeacherRecording ? '/teacher/dashboard' : '/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          
          
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {!recordingComplete && (
            <div className="relative bg-gradient-to-br from-white via-indigo-50 to-purple-50 shadow-2xl rounded-2xl p-8 mb-8 border border-indigo-100 overflow-hidden">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-200 to-rose-200 rounded-full translate-y-12 -translate-x-12 opacity-20"></div>
              
              <div className="relative">
                <div className="flex items-center mb-8">
                  <div className="relative">
                    <div className="text-5xl mr-6 animate-pulse">🎵</div>
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-400 rounded-full animate-ping"></div>
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Record Your Lesson</h2>
                    <p className="text-gray-600 text-lg mt-2">Capture your lesson audio and let AI create a comprehensive summary with practice reminders and key insights. Click start when you're ready to begin recording.</p>
                  </div>
                </div>
              
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl p-4 mb-6 shadow-lg">
                <div className="flex items-center">
                  <div className="text-2xl mr-3">⏰</div>
                  <p className="text-sm font-medium">
                    <strong>Maximum Recording Time:</strong> 70 minutes (recordings will automatically stop at this limit)
                  </p>
                </div>
              </div>
              
              <div className="text-center mb-8">
                <div className="relative bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-600 text-white rounded-3xl p-10 shadow-2xl mb-6 overflow-hidden">
                  {/* Animated background elements */}
                  <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-4 left-4 w-2 h-2 bg-white/30 rounded-full animate-ping"></div>
                    <div className="absolute top-8 right-8 w-1 h-1 bg-white/40 rounded-full animate-pulse"></div>
                    <div className="absolute bottom-6 left-8 w-1.5 h-1.5 bg-white/20 rounded-full animate-ping delay-1000"></div>
                    <div className="absolute bottom-4 right-4 w-1 h-1 bg-white/30 rounded-full animate-pulse delay-500"></div>
                  </div>
                  
                  <div className="relative">
                    <div className="text-7xl font-bold mb-4 font-mono tracking-wider">
                      {formatTime(recordingTime)}
                    </div>
                    <div className="text-xl font-medium">
                      {isRecording ? (
                        <span className="flex items-center justify-center">
                          <div className="w-4 h-4 bg-red-400 rounded-full animate-pulse mr-3 shadow-lg"></div>
                          <span className="animate-pulse">Recording in progress...</span>
                        </span>
                      ) : isPaused ? (
                        <span className="flex items-center justify-center">
                          <div className="w-4 h-4 bg-yellow-400 rounded-full mr-3 shadow-lg"></div>
                          <span>Recording paused</span>
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <div className="w-4 h-4 bg-green-400 rounded-full mr-3 shadow-lg"></div>
                          <span>Ready to start recording</span>
                        </span>
                      )}
                      {recordingTime > 3600 && recordingTime < 4200 && (
                        <span className="text-yellow-200 ml-3 text-lg">⚠️ Approaching 70-minute limit</span>
                      )}
                      {maxRecordingReached && (
                        <span className="text-red-200 ml-3 text-lg">⏰ Recording stopped at 70 minutes</span>
                      )}
                    </div>
                  </div>
                </div>
                {(recordingTime > 0 || estimatedFileSize > 0) && (
                  <div className="text-xs text-gray-400">
                    Estimated file size: {formatFileSize(estimatedFileSize)}
                    {estimatedFileSize > 24000 && (
                      <span className="text-orange-600 ml-1">⚠️ Close to 25MB limit</span>
                    )}
                  </div>
                )}
                
                {/* 70-minute limit progress bar */}
                {recordingTime > 0 && (
                  <div className="mt-8 bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                    <div className="flex justify-between text-sm text-white mb-3">
                      <span className="font-semibold text-lg">Recording Progress</span>
                      <span className="font-bold text-lg">{Math.round((recordingTime / 4200) * 100)}% of 70-minute limit</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-4 shadow-inner">
                      <div 
                        className={`h-4 rounded-full transition-all duration-1000 shadow-lg relative overflow-hidden ${
                          recordingTime > 3600 ? 'bg-gradient-to-r from-orange-400 to-red-500' : 'bg-gradient-to-r from-indigo-400 to-purple-500'
                        }`}
                        style={{ width: `${Math.min(100, (recordingTime / 4200) * 100)}%` }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                      </div>
                    </div>
                    <div className="mt-3 text-center text-white/80 text-sm">
                      {recordingTime < 3600 ? '🟢 Safe recording time' : recordingTime < 4200 ? '🟡 Approaching limit' : '🔴 Time limit reached'}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-center space-x-6">
                {!isRecording && !isPaused && (
                  <button
                    onClick={startRecording}
                    className="group relative bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-10 py-5 rounded-2xl font-bold text-xl shadow-2xl transform hover:scale-110 transition-all duration-300 overflow-hidden animate-pulse"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative flex items-center">
                      <div className="text-3xl mr-3 group-hover:animate-bounce">🎙️</div>
                      <span>Start Recording</span>
                    </div>
                  </button>
                )}

                {isRecording && (
                  <>
                    <button
                      onClick={pauseRecording}
                      className="group relative bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-8 py-5 rounded-2xl font-bold text-lg shadow-2xl transform hover:scale-110 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex items-center">
                        <div className="text-2xl mr-2 group-hover:animate-pulse">⏸️</div>
                        <span>Pause</span>
                      </div>
                    </button>
                    <button
                      onClick={stopRecording}
                      className="group relative bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white px-8 py-5 rounded-2xl font-bold text-lg shadow-2xl transform hover:scale-110 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-rose-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex items-center">
                        <div className="text-2xl mr-2 group-hover:animate-pulse">⏹️</div>
                        <span>Stop</span>
                      </div>
                    </button>
                  </>
                )}

                {isPaused && (
                  <>
                    <button
                      onClick={resumeRecording}
                      className="group relative bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-8 py-5 rounded-2xl font-bold text-lg shadow-2xl transform hover:scale-110 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex items-center">
                        <div className="text-2xl mr-2 group-hover:animate-bounce">▶️</div>
                        <span>Resume</span>
                      </div>
                    </button>
                    <button
                      onClick={stopRecording}
                      className="group relative bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white px-8 py-5 rounded-2xl font-bold text-lg shadow-2xl transform hover:scale-110 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-rose-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex items-center">
                        <div className="text-2xl mr-2 group-hover:animate-pulse">⏹️</div>
                        <span>Stop</span>
                      </div>
                    </button>
                  </>
                )}
              </div>
              </div>
            </div>
          )}

          {recordingComplete && !lessonData && (
            <div className="relative bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 shadow-2xl rounded-2xl p-8 mb-8 border border-green-200 overflow-hidden">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-200 to-emerald-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-teal-200 to-cyan-200 rounded-full translate-y-12 -translate-x-12 opacity-30"></div>
              
              <div className="relative">
                <div className="flex items-center mb-8">
                  <div className="relative">
                    <div className="text-5xl mr-6 animate-bounce">🎉</div>
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-400 rounded-full animate-ping"></div>
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">Recording Complete!</h2>
                    <p className="text-gray-600 text-lg mt-2">Your lesson has been successfully recorded and is ready for processing</p>
                  </div>
                </div>
              
              {audioUrl && (
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl p-4 mb-4">
                    <div className="flex items-center">
                      <div className="text-2xl mr-3">⏱️</div>
                      <p className="text-lg font-medium">Recording Duration: {formatTime(recordingTime)}</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-lg">
                    <audio controls src={audioUrl} className="w-full">
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                </div>
              )}

              <div className="flex justify-center space-x-8">
                <button
                  onClick={processLesson}
                  disabled={isProcessing}
                  className="group relative bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-600 hover:from-indigo-600 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-10 py-5 rounded-2xl font-bold text-xl shadow-2xl transform hover:scale-110 transition-all duration-300 disabled:transform-none overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center">
                    <div className="text-3xl mr-3 group-hover:animate-spin">✨</div>
                    <span>{isProcessing ? 'Processing...' : 'Create Summary & Tasks'}</span>
                  </div>
                </button>
                <button
                  onClick={resetRecording}
                  className="group relative bg-gradient-to-r from-slate-500 to-gray-600 hover:from-slate-600 hover:to-gray-700 text-white px-8 py-5 rounded-2xl font-bold text-lg shadow-2xl transform hover:scale-110 transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-slate-400 to-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center">
                    <div className="text-2xl mr-2 group-hover:animate-pulse">🔄</div>
                    <span>Record Again</span>
                  </div>
                </button>
              </div>
              </div>
            </div>
          )}

          {isProcessing && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 shadow-xl rounded-xl p-8 mb-6 text-center border border-purple-200">
              <div className="text-6xl mb-4 animate-spin">⚡</div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">Processing Your Lesson</h3>
              <p className="text-lg text-gray-700">
                We're transcribing your audio and generating a summary, task list, and quiz...
              </p>
              <div className="mt-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg p-3">
                <div className="flex items-center justify-center">
                  <div className="text-xl mr-2">🤖</div>
                  <span className="font-medium">AI is working its magic!</span>
                </div>
              </div>
            </div>
          )}

          {lessonData && (lessonData.overview || lessonData.summaryTxt) && (
            <div className="space-y-8">
              {/* Lesson Overview */}
              <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-xl p-8 border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="text-3xl mr-4">📝</div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Lesson Overview</h3>
                </div>
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6">
                  <p className="text-gray-800 whitespace-pre-wrap text-lg leading-relaxed">{lessonData.overview || lessonData.summaryTxt}</p>
                </div>
              </div>

              {/* Practice Reminders */}
              {lessonData.practiceReminders && Array.isArray(lessonData.practiceReminders) && lessonData.practiceReminders.length > 0 && (
                <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-xl p-8 border border-gray-100">
                  <div className="flex items-center mb-6">
                    <div className="text-3xl mr-4">💡</div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Practice Reminders</h3>
                  </div>
                  <div className="space-y-4">
                    {lessonData.practiceReminders.map((item, index) => {
                      if (item.type === 'section') {
                        return (
                          <div key={index} className="mt-6 mb-4">
                            <h5 className="text-xl font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 rounded-xl shadow-lg">
                              {item.content}
                            </h5>
                          </div>
                        );
                      } else if (item.type === 'subsection') {
                        return (
                          <div key={index} className="ml-6">
                            <h6 className="text-lg font-semibold text-green-700 bg-green-100 px-4 py-2 rounded-lg">
                              {item.content}
                            </h6>
                          </div>
                        );
                      } else if (item.type === 'bullet') {
                        return (
                          <div key={index} className="flex items-start ml-8">
                            <span className="text-green-600 mr-3 text-xl">•</span>
                            <span className="text-gray-800 text-lg">{item.content}</span>
                          </div>
                        );
                      } else {
                        return (
                          <div key={index} className="text-gray-800 text-lg bg-gray-50 rounded-lg p-4">
                            {item.content}
                          </div>
                        );
                      }
                    })}
                  </div>
                </div>
              )}

              {/* Key Mindset Messages */}
              {lessonData.mindsetMessages && (
                <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-xl p-8 border border-gray-100">
                  <div className="flex items-center mb-6">
                    <div className="text-3xl mr-4">🌟</div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">Key Mindset Messages</h3>
                  </div>
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6">
                    <div className="text-gray-800 whitespace-pre-wrap text-lg leading-relaxed">
                      {lessonData.mindsetMessages}
                    </div>
                  </div>
                </div>
              )}

              {/* Key Quotes */}
              {lessonData.keyQuotes && Array.isArray(lessonData.keyQuotes) && lessonData.keyQuotes.length > 0 && (
                <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-xl p-8 border border-gray-100">
                  <div className="flex items-center mb-6">
                    <div className="text-3xl mr-4">💬</div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Key Quotes</h3>
                  </div>
                  <div className="space-y-4">
                    {lessonData.keyQuotes.map((quote, index) => (
                      <div key={index} className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6 border-l-4 border-pink-400">
                        <blockquote className="text-gray-800 text-lg italic leading-relaxed">
                          "{quote}"
                        </blockquote>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-center mt-8">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center">
                    <div className="text-2xl mr-2">🏠</div>
                    Back to Dashboard
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Progress Modal */}
          {showProgressModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-gray-200">
                <div className="text-center">
                  <div className="mb-6">
                    <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                      <div className="text-4xl animate-spin">⚡</div>
                    </div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">Processing Your Lesson</h3>
                    <p className="text-gray-700 text-lg mb-6 font-medium">{processingStage}</p>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-4 mb-4 shadow-inner">
                    <div 
                      className="bg-gradient-to-r from-indigo-500 to-purple-600 h-4 rounded-full transition-all duration-500 ease-out shadow-lg"
                      style={{ width: `${processingProgress}%` }}
                    ></div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg p-3">
                    <p className="text-lg font-bold">{processingProgress}% Complete</p>
                  </div>
                  
                  {/* Processing Steps */}
                  <div className="mt-6 space-y-2 text-left">
                    <div className={`flex items-center text-sm ${processingProgress >= 20 ? 'text-green-600' : 'text-gray-400'}`}>
                      <div className={`w-4 h-4 rounded-full mr-3 ${processingProgress >= 20 ? 'bg-green-500' : 'bg-gray-300'}`}>
                        {processingProgress >= 20 && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      Uploading audio file
                    </div>
                    <div className={`flex items-center text-sm ${processingProgress >= 50 ? 'text-green-600' : 'text-gray-400'}`}>
                      <div className={`w-4 h-4 rounded-full mr-3 ${processingProgress >= 50 ? 'bg-green-500' : 'bg-gray-300'}`}>
                        {processingProgress >= 50 && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      Setting up lesson document
                    </div>
                    <div className={`flex items-center text-sm ${processingProgress >= 70 ? 'text-green-600' : 'text-gray-400'}`}>
                      <div className={`w-4 h-4 rounded-full mr-3 ${processingProgress >= 70 ? 'bg-green-500' : 'bg-gray-300'}`}>
                        {processingProgress >= 70 && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      Transcribing audio and generating summary
                    </div>
                    <div className={`flex items-center text-sm ${processingProgress >= 90 ? 'text-green-600' : 'text-gray-400'}`}>
                      <div className={`w-4 h-4 rounded-full mr-3 ${processingProgress >= 90 ? 'bg-green-500' : 'bg-gray-300'}`}>
                        {processingProgress >= 90 && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      Finalizing lesson data
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Audio Recording Test - Full Width Design with Colored Header */}
          <div className="mt-8">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              {/* Colored Header */}
              <div className="bg-gradient-to-r from-slate-500 to-gray-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-3xl mr-3">🔧</div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Audio Recording Test</h3>
                      <p className="text-sm text-slate-100">Test your microphone and audio settings before recording</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-200 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">Optional</span>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <AudioTest />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RecordLesson;
